import {Injectable, signal} from '@angular/core';
import {FormBlock, FormDefinition} from '../../shared/models/form-schema.model';
import {
  ContainerVisibility,
  emptyVerdict,
  evaluateFormVisibility,
  FieldVisibility,
  pathId,
  StaticTextVisibility,
  VisibilityVerdict
} from '../../shared/services/visibility-evaluator';

/**
 * Holds the **one** conditional-logic verdict a running form is driven by (#470).
 *
 * Provided by {@link DynamicFormComponent} (a component-level provider, so two forms on one page never share a
 * verdict) and injected **optionally** by {@link BlockRendererComponent}. That is what makes the rendered
 * visibility and the validation/submit visibility structurally incapable of disagreeing: the renderer no longer
 * decides anything, it reads the verdict the form already computed for the whole definition.
 *
 * A renderer used outside a `DynamicFormComponent` (a story, an isolated spec) gets `null` here and falls back
 * to evaluating its own block — there is nothing to disagree with in that case.
 */
@Injectable()
export class FormVisibilityState {
  /** The current verdict. A signal so consumers can read it during change detection without a subscription. */
  readonly verdict = signal<VisibilityVerdict>(emptyVerdict());

  /** Recompute the verdict for `definition` against the form's current raw value. */
  update(definition: FormDefinition | null | undefined, rawValue: any): VisibilityVerdict {
    const next = evaluateFormVisibility(definition, rawValue);
    this.verdict.set(next);
    return next;
  }

  /** The verdict for one block path, or `null` when the path has no entry. */
  at(path: readonly string[]): FieldVisibility | ContainerVisibility | StaticTextVisibility | null {
    return this.verdict().byPath.get(pathId(path)) ?? null;
  }

  /**
   * Whether the block at `path` currently applies.
   *
   * **A missing entry is never "visible" for a block that carries a rule (#490).** The old signature took only a
   * path and answered `?? true`, so a block the evaluator never emitted rendered unconditionally — which is
   * exactly how every conditional `static-text` ended up permanently on screen, with nothing to notice it.
   *
   * With the block in hand the fallback is answerable rather than guessed: no rule ⇒ it was always going to be
   * visible; a rule ⇒ we have no verdict for a block that is supposed to be gated, so refuse to show it. The
   * only correct source of "yes" for a conditional block is a real verdict entry.
   *
   * `BlockRendererComponent` does one better and re-evaluates the block's own rule locally instead — see its
   * `evaluateRules`.
   */
  isBlockVisible(block: FormBlock, path: readonly string[]): boolean {
    const entry = this.at(path);
    return entry ? entry.visible : !block.visibleWhen;
  }
}
